import { useState } from "react";
import { useLocation } from "wouter";
import { getSupabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

export default function SetPassword() {
  const [, setLocation] = useLocation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setSaving(true);
    try {
      const supabase = await getSupabase();

      // If arrived via email link, verify the OTP token first to establish session.
      // Staff invites and password resets both link here with a token_hash (v1.5.60).
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params.get("token_hash");
      const type = params.get("type");
      if (tokenHash && (type === "recovery" || type === "invite")) {
        const { data: otpData, error: otpError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });
        console.log("[SetPassword] verifyOtp result:", { user: otpData?.user?.email, error: otpError?.message });
        if (otpError) {
          const expired = /expired|invalid/i.test(otpError.message || "");
          throw new Error(
            expired
              ? "This link has expired or was already used. Ask an admin to resend your invite, or use \"Forgot password?\" on the sign-in page."
              : `Token verification failed: ${otpError.message}`
          );
        }
      }

      const { data: updateData, error: updateError } = await supabase.auth.updateUser({ password });
      console.log("[SetPassword] updateUser result:", { user: updateData?.user?.email, error: updateError?.message });
      if (updateError) throw new Error(`Password update failed: ${updateError.message}`);

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Failed to set password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-heading font-bold">Set your password</h1>
          <p className="text-sm text-muted-foreground">Choose a password to complete your account setup.</p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Create password</CardTitle>
            <CardDescription>Must be at least 8 characters.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="password">New password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>

              {error && (
                <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                  <p className="text-sm text-destructive font-medium">{error}</p>
                  <p className="text-xs text-destructive/70 mt-1">Check browser console (F12) for details.</p>
                </div>
              )}

              {success ? (
                <div className="space-y-3">
                  <div className="rounded-md bg-green-50 border border-green-200 p-3">
                    <p className="text-sm text-green-800 font-medium">Password updated successfully!</p>
                    <p className="text-xs text-green-700 mt-1">You can now sign in with your new password.</p>
                  </div>
                  <Button type="button" className="w-full" onClick={() => setLocation("/login")}>
                    Go to Sign In
                  </Button>
                </div>
              ) : (
                <Button type="submit" className="w-full" disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                  Set Password & Sign In
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
